import type {
  HekateFormDefinition,
  HekateBusFieldValue,
  HekateEventBusCallbackParams,
  HekateDisplayField,
} from '@onshape/www-inertia';
import { FieldHtmlNameUtil, HekateDisplayForm, FormIdUtil, Trans } from '@onshape/www-inertia';

import hekateBus from '../../../../bus';
import ExternalUtil from '../../../ExternalUtil';
import ProgressiveProfiler from '../progressive-profiles/ProgressiveProfiler';
import type { OnSubmitFormFunction } from '../../../../model/OnSubmitFormFunction';
import { isSignupTestEnabled } from '~/hekate-www/external/util/E2eTestingUtil';

export default class FormAppCoordinator {
  private readonly callId: Ref<number>;

  private readonly formDefinition: Ref<HekateDisplayForm | null>;

  private readonly busFieldValues: Ref<HekateBusFieldValue[]>;

  private readonly appElement: Ref<HTMLFormElement | null>;

  private readonly isFormUnloadable: Ref<boolean>;

  private readonly formNotValidErrorMsg: Ref<string>;

  private readonly onSubmitForm: OnSubmitFormFunction;

  private readonly formId: string;

  private readonly formVariant: string;

  private readonly hiddenStaticField1: string;

  private readonly hiddenStaticField2: string;

  constructor({
    formDefinition,
    busFieldValues,
    appElement,
    onSubmitForm,
    isFormUnloadable,
    formId,
    formVariant,
    hiddenStaticField1,
    hiddenStaticField2,
    formNotValidErrorMsg,
  }: {
    formDefinition: Ref<HekateDisplayForm | null>;
    busFieldValues: Ref<HekateBusFieldValue[]>;
    appElement: Ref<HTMLFormElement | null>;
    onSubmitForm: OnSubmitFormFunction;
    isFormUnloadable: Ref<boolean>;
    formNotValidErrorMsg: Ref<string>;
    formId: string;
    formVariant: string;
    hiddenStaticField1: string;
    hiddenStaticField2: string;
  }) {
    this.callId = ref(0);
    this.formDefinition = formDefinition;
    this.busFieldValues = busFieldValues;
    this.appElement = appElement;
    this.onSubmitForm = onSubmitForm;
    this.isFormUnloadable = isFormUnloadable;
    this.formId = formId;
    this.formVariant = formVariant;
    this.hiddenStaticField1 = hiddenStaticField1;
    this.hiddenStaticField2 = hiddenStaticField2;
    this.formNotValidErrorMsg = formNotValidErrorMsg;
  }

  private handleFormFieldValues(values: HekateBusFieldValue[]) {
    values.forEach((newVal) => {
      const index = this.busFieldValues.value.findIndex(
        (oldVal) => oldVal.fieldName === newVal.fieldName
      );
      if (index !== -1) {
        this.busFieldValues.value.splice(index, 1, newVal);
      } else {
        this.busFieldValues.value.push(newVal);
      }
    });
  }

  /** Watch the field values that are sent via the `hekateEventBus` to set the display field */
  private watchBusFieldValues() {
    watchEffect(() => {
      this.busFieldValues.value.forEach((filedValue) => {
        if (this.formDefinition.value === null) {
          return;
        }
        const index = this.formDefinition.value.displayFields.findIndex(
          (displayField: HekateDisplayField) => displayField.htmlName === filedValue.fieldName
        );
        if (index === -1) {
          return;
        }
        if (this.formDefinition.value.displayFields[index]?.isIgnorePrefillAndEventBusValues) {
          return;
        }
        this.formDefinition.value.displayFields[index]!.val = filedValue.fieldValue;
      });
    });
  }

  /**
   * Form definitions are sent as events so that we can get them from the
   * server, or when previewing in the formfort.web.app
   */
  private handleFormDefinition(formDefinition: HekateFormDefinition | Record<string, never>) {
    try {
      this.innerHandleFormDefinition(formDefinition);
    } catch (e) {
      console.error(e);
      throw e;
    }
  }

  /**
   * Form definitions are sent as events so that we can get them from the
   * server, or when previewing in the formfort.web.app
   */
  private innerHandleFormDefinition(formDefinition: HekateFormDefinition | Record<string, never>) {
    if (Object.keys(formDefinition).length === 0) {
      this.formDefinition.value = null;
    } else {
      // note the `this.formId` is empty in formfort (so we ignore this check when previewing on formfort)
      if (this.formId && formDefinition.id !== this.formId) {
        return;
      }

      const tmpFormDefinition = new HekateDisplayForm(formDefinition as HekateFormDefinition);
      if (isSignupTestEnabled()) {
        tmpFormDefinition.isRecaptcha = false;
      }

      this.formDefinition.value = tmpFormDefinition;
      ProgressiveProfiler.initDisplay({
        formDefinition: this.formDefinition,
        formNotValidErrorMsg: this.formNotValidErrorMsg,
      });

      [
        { fieldName: FieldHtmlNameUtil.formVariant, val: this.formVariant },
        { fieldName: FieldHtmlNameUtil.hiddenStaticField1, val: this.hiddenStaticField1 },
        { fieldName: FieldHtmlNameUtil.hiddenStaticField2, val: this.hiddenStaticField2 },
      ].forEach((item) => {
        const itemField = ExternalUtil.findDisplayFieldOrNull({
          formDefinition: this.formDefinition,
          fieldName: item.fieldName,
        });

        if (itemField) {
          itemField.val = item.val;
        }
      });
    }

    this.callId.value += 1;

    hekateBus.dispatchEvent('form-ready', {
      formDefinition: { id: this.formDefinition.value!.id },
      isReady: true,
    });
  }

  setup() {
    this.watchBusFieldValues();

    hekateBus.addEventListener(
      'form-definition',
      (args: HekateEventBusCallbackParams<HekateFormDefinition>) => {
        this.handleFormDefinition(args.data);
      }
    );

    hekateBus.addEventListener(
      'form-inject-value',
      (args: HekateEventBusCallbackParams<HekateBusFieldValue>) => {
        this.handleFormFieldValues([args.data]);
      }
    );

    // This is a quick and dirty implementation and should be refactored to
    // support error reporting via "sent" and to get rid of the `setTimeout`
    // NOTE: ALL forms on the page will be submitted!
    hekateBus.addEventListener(
      'form-inject-and-submit',
      (args: HekateEventBusCallbackParams<HekateBusFieldValue[]>) => {
        this.handleFormFieldValues(args.data);
        // Have to wait for the `watchBusFields` to finish.
        setTimeout(async () => {
          await this.onSubmitForm(new CustomEvent('HekateBusSubmit'));
          hekateBus.dispatchEvent('form-inject-and-submit-sent', {}); // todo form id
        }, 50);
      }
    );

    const isUnifiedSignupForm = computed(
      () =>
        this.formDefinition &&
        FormIdUtil.isUnifiedSignupForm({ formId: this.formDefinition.value!.id })
    );
    const isTranslationReady = ref(false);
    Trans.translationIsReady().then((rr) => {
      isTranslationReady.value = rr;
    });

    return {
      formDefinition: this.formDefinition,
      appElement: this.appElement,
      callId: this.callId,
      isFormUnloadable: this.isFormUnloadable,
      formNotValidErrorMsg: this.formNotValidErrorMsg,
      isUnifiedSignupForm,
      isTranslationReady,
    };
  }
}
